import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  useToast,
} from "@payflow/ui";
import { useAuth } from "../../contexts/AuthContext";
import { updateProfile, changePassword } from "../../services/auth.service";

export function ProfilePage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<"info" | "password">("info");
  const [saving, setSaving] = useState(false);

  // Form states
  const [profileData, setProfileData] = useState({
    nome: user?.nome || "",
    email: user?.email || "",
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const response = await updateProfile({
        nome: profileData.nome,
        email: profileData.email,
      });
      if (response.data) {
        setProfileData({
          nome: response.data.nome,
          email: response.data.email,
        });
        // Update localStorage to reflect changes immediately
        const cached = localStorage.getItem("payflow_user");
        if (cached) {
          const userData = JSON.parse(cached);
          userData.nome = response.data.nome;
          userData.email = response.data.email;
          localStorage.setItem("payflow_user", JSON.stringify(userData));
        }
      }
      toast({ variant: "success", title: t("profile.save_success") });
    } catch (err) {
      toast({ variant: "error", title: err instanceof Error ? err.message : t("profile.save_error") });
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast({ variant: "error", title: t("profile.password_mismatch") });
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast({ variant: "error", title: t("profile.password_min_length") });
      return;
    }

    setSaving(true);
    try {
      await changePassword({
        senhaAtual: passwordData.currentPassword,
        novaSenha: passwordData.newPassword,
      });
      toast({ variant: "success", title: t("profile.password_success") });
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err) {
      toast({ variant: "error", title: err instanceof Error ? err.message : t("profile.password_error") });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">{t("profile.title")}</h1>
        <p className="text-muted-foreground">
          {t("profile.subtitle")}
        </p>
      </div>

      {/* User info card */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-primary">
              <span className="text-2xl font-bold text-white">
                {user?.nome?.charAt(0) || "U"}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                {user?.nome}
              </h2>
              <p className="text-muted-foreground">{user?.email}</p>
              <span className="mt-1 inline-block rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                {user?.nivel}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="mb-6 flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab("info")}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === "info"
              ? "border-b-2 border-primary-500 text-primary-600"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("profile.tab_info")}
        </button>
        <button
          onClick={() => setActiveTab("password")}
          className={`px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === "password"
              ? "border-b-2 border-primary-500 text-primary-600"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {t("profile.tab_password")}
        </button>
      </div>

      {/* Tab content */}
      {activeTab === "info" && (
        <Card>
          <CardHeader>
            <CardTitle>{t("profile.info_title")}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSaveProfile} className="space-y-4">
              <Input
                label={t("profile.label_name")}
                value={profileData.nome}
                onChange={(e) =>
                  setProfileData({ ...profileData, nome: e.target.value })
                }
                required
              />

              <Input
                label={t("profile.label_email")}
                type="email"
                value={profileData.email}
                onChange={(e) =>
                  setProfileData({ ...profileData, email: e.target.value })
                }
                required
              />

              <div className="flex justify-end pt-4">
                <Button type="submit" loading={saving}>
                  {t("profile.save")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {activeTab === "password" && (
        <Card>
          <CardHeader>
            <CardTitle>{t("profile.password_title")}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              <Input
                label={t("profile.label_current_password")}
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) =>
                  setPasswordData({
                    ...passwordData,
                    currentPassword: e.target.value,
                  })
                }
                required
              />

              <Input
                label={t("profile.label_new_password")}
                type="password"
                value={passwordData.newPassword}
                onChange={(e) =>
                  setPasswordData({
                    ...passwordData,
                    newPassword: e.target.value,
                  })
                }
                required
              />

              <Input
                label={t("profile.label_confirm_password")}
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) =>
                  setPasswordData({
                    ...passwordData,
                    confirmPassword: e.target.value,
                  })
                }
                required
              />

              <div className="flex justify-end pt-4">
                <Button type="submit" loading={saving}>
                  {t("profile.change_password")}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Permissions info */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-sm">{t("profile.permissions_title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {user?.permissoes.map((perm) => (
              <span
                key={perm}
                className="rounded-full bg-muted px-2 py-1 text-xs text-muted-foreground"
              >
                {perm}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
